import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Steps,
  Typography,
  Tag,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  LinkOutlined,
  RocketOutlined,
  SettingOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import Select from "react-select";
import { useLocation } from "react-router-dom";
import { channelsApi } from "../api/channelsApi";
import ChannexIframeModal from "../components/ChannexIframeModal";

const { Title, Text } = Typography;

const OTA_FILTER_MAP = {
  booking: "BDC",
  bookingcom: "BDC",
  "booking.com": "BDC",
  expedia: "EXP",
  agoda: "AGO",
  airbnb: "ABB",
};

const normalizeCode = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/_/g, "")
    .replace(/-/g, "")
    .replace(/\./g, "");

export default function SetupWizard({ onComplete }) {
  const location = useLocation();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [channels, setChannels] = useState([]);
  const [pmsRooms, setPmsRooms] = useState([]);

  const [selectedChannel, setSelectedChannel] = useState(null);
  const [hotelId, setHotelId] = useState("");

  const [connectResult, setConnectResult] = useState(null);
  const [iframeOpen, setIframeOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);

  const selectedChannelObj = useMemo(
    () => channels.find((c) => c.code === selectedChannel),
    [channels, selectedChannel]
  );

  const selectedChannelLabel = selectedChannelObj?.name || "Selected channel";

  const steps = useMemo(
    () => [
      { title: "Choose Channel", icon: <RocketOutlined /> },
      { title: "Connection Details", icon: <SettingOutlined /> },
      { title: "Auto-Provision", icon: <SafetyOutlined /> },
      { title: "Map & Activate", icon: <LinkOutlined /> },
      { title: "Verify Booking Sync", icon: <CheckCircleOutlined /> },
    ],
    []
  );

  const channelOptions = useMemo(
    () =>
      (channels || []).map((c) => ({
        value: c.code,
        label: c.name,
        is_connected: !!c.is_connected,
        meta: c,
      })),
    [channels]
  );

  const selectedOption = useMemo(() => {
    if (!selectedChannel) return null;
    return channelOptions.find((o) => o.value === selectedChannel) || null;
  }, [selectedChannel, channelOptions]);

  const rsStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 48,
        borderRadius: 14,
        borderColor: state.isFocused ? "rgba(59,130,246,.55)" : "rgba(15,23,42,.12)",
        boxShadow: state.isFocused ? "0 0 0 4px rgba(59,130,246,.14)" : "none",
        ":hover": { borderColor: "rgba(15,23,42,.20)" },
        background: "#fff",
      }),
      valueContainer: (base) => ({ ...base, padding: "0 12px" }),
      placeholder: (base) => ({ ...base, color: "rgba(15,23,42,.45)", fontWeight: 650 }),
      singleValue: (base) => ({ ...base, fontWeight: 800, color: "#0f172a" }),
      menu: (base) => ({
        ...base,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 18px 60px rgba(15,23,42,.16)",
        zIndex: 9999,
      }),
      option: (base, state) => ({
        ...base,
        padding: "12px 12px",
        background: state.isSelected
          ? "rgba(59,130,246,.10)"
          : state.isFocused
          ? "rgba(15,23,42,.04)"
          : "#fff",
        color: "#0f172a",
        cursor: "pointer",
      }),
      indicatorSeparator: () => ({ display: "none" }),
    }),
    []
  );

  useEffect(() => {
    (async () => {
      try {
        const [ch, rooms] = await Promise.all([
          channelsApi.listCompanyChannels(),
          channelsApi.listPmsRoomTypes(),
        ]);

        const channelList = ch.data?.data || [];
        setChannels(channelList);
        setPmsRooms(rooms.data?.data || []);

        const params = new URLSearchParams(location.search);
        const rawChannel = params.get("channel");
        const normalized = normalizeCode(rawChannel);

        if (normalized) {
          const matched = channelList.find((c) => normalizeCode(c.code) === normalized);
          if (matched) {
            setSelectedChannel(matched.code);
            setStep(1);
          }
        }
      } catch (e) {
        message.error(e?.response?.data?.message || "Failed to load channels and room types.");
      }
    })();
  }, [location.search]);

  async function handleConnectAndProvision() {
    if (!selectedChannel || !hotelId.trim()) {
      return message.error("Please enter the channel hotel ID.");
    }

    setLoading(true);
    try {
      const payload = {
        channel_code: selectedChannel,
        hotel_id: hotelId.trim(),
        settings: { pricing: {}, currency: "GBP" },
      };

      const resp = await channelsApi.connect(payload);
      setConnectResult(resp.data);
      setIframeUrl(resp.data?.iframe_url || "");
      message.success("Connection saved and inventory provisioned.");
      setStep(3);
    } catch (e) {
      message.error(e.response?.data?.message || "Connect failed");
    } finally {
      setLoading(false);
    }
  }

  async function openEmbeddedSetup() {
    setLoading(true);
    try {
      const filterCode = OTA_FILTER_MAP[normalizeCode(selectedChannel)] || null;

      const resp = await channelsApi.iframeToken({
        provider_code: "channex",
        redirect_to: "/channels",
        channels: filterCode,
      });

      setIframeUrl(resp.data?.iframe_url || "");
      setIframeOpen(true);
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to open setup.");
    } finally {
      setLoading(false);
    }
  }

  async function verifySync() {
    setLoading(true);
    try {
      const resp = await channelsApi.syncBookingRevisions({
        provider_code: "channex",
        ack: false,
        limit: 20,
      });

      setVerifyResult(resp.data || null);
      message.success(`Checked booking feed. Found ${resp.data?.count || 0} revision(s).`);
      onComplete?.(resp.data);
    } catch (e) {
      message.error(e.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid" style={{ maxWidth: 1200, paddingTop: 18 }}>
        <div style={{ marginBottom: 14 }}>
          <Title level={2} style={{ marginBottom: 6 }}>
            {selectedChannelObj?.name ? `Connect ${selectedChannelObj.name}` : "Connect Channel"}
          </Title>
          <Text type="secondary">
            Guided onboarding to connect the channel, provision inventory, and activate booking sync.
          </Text>
        </div>

        <Card style={{ borderRadius: 18 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Tag color="blue" style={{ margin: 0, borderRadius: 999, padding: "4px 10px", fontWeight: 800 }}>
                Step {step + 1}/{steps.length}
              </Tag>
              <Text style={{ fontWeight: 900, fontSize: 15, color: "#0f172a" }}>
                {steps[step]?.title}
              </Text>
            </div>

            {!!selectedChannelObj?.name && (
              <Text type="secondary">
                Selected: <b style={{ color: "#0f172a" }}>{selectedChannelObj.name}</b>
              </Text>
            )}
          </div>

          <div className="ns-stepsWrap">
            <Steps current={step} items={steps} responsive />
          </div>

          <Divider style={{ marginTop: 14 }} />

          {step === 0 && (
            <>
              <Text strong>Select the channel you want to connect</Text>
              <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                This determines which OTA will receive inventory, rates, and bookings.
              </Text>

              <div style={{ maxWidth: 820 }}>
                <Select
                  value={selectedOption}
                  onChange={(opt) => setSelectedChannel(opt?.value || null)}
                  options={channelOptions}
                  styles={rsStyles}
                  isSearchable
                  placeholder="Search and select a channel…"
                  formatOptionLabel={(opt) => (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <div style={{ fontWeight: 900 }}>{opt.label}</div>
                        <div style={{ fontSize: 12, color: "rgba(15,23,42,.60)" }}>
                          {opt.is_connected ? "Already connected" : "Available to connect"}
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 900,
                          padding: "4px 10px",
                          borderRadius: 999,
                          border: `1px solid ${opt.is_connected ? "rgba(34,197,94,.28)" : "rgba(15,23,42,.14)"}`,
                          background: opt.is_connected ? "rgba(34,197,94,.10)" : "rgba(15,23,42,.03)",
                          color: opt.is_connected ? "rgba(22,163,74,1)" : "rgba(15,23,42,.68)",
                        }}
                      >
                        {opt.is_connected ? "Connected" : "Ready"}
                      </div>
                    </div>
                  )}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <Button
                  type="primary"
                  disabled={!selectedChannel}
                  onClick={() => setStep(1)}
                  style={{ borderRadius: 12, height: 42, paddingInline: 18, fontWeight: 900 }}
                >
                  Continue
                </Button>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <Card style={{ borderRadius: 16, background: "#fafafa" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <Form layout="vertical">
                    <Form.Item label={`${selectedChannelLabel} Hotel ID`} required>
                      <Input value={hotelId} onChange={(e) => setHotelId(e.target.value)} />
                    </Form.Item>
                  </Form>

                  <div>
                    <Alert
                      type="info"
                      showIcon
                      message="Why we need this"
                      description="This hotel ID is used to open the channel’s connection settings and load rooms and rates for mapping."
                      style={{ borderRadius: 14, marginBottom: 12 }}
                    />

                    <Alert
                      type="success"
                      showIcon
                      message="Integration account is ready"
                      description="Provider-level connection details are already configured for this company. You only need the channel-specific hotel ID here."
                      style={{ borderRadius: 14 }}
                    />
                  </div>
                </div>
              </Card>

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                <Button onClick={() => setStep(0)} style={{ borderRadius: 12 }}>
                  Back
                </Button>
                <Button
                  type="primary"
                  disabled={!hotelId.trim()}
                  onClick={() => setStep(2)}
                  style={{ borderRadius: 12, fontWeight: 900 }}
                >
                  Continue
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Card style={{ borderRadius: 16, background: "#0b1220", color: "white" }}>
                <Title level={4} style={{ color: "white", marginTop: 0 }}>
                  Auto-Provision Inventory
                </Title>
                <Text style={{ color: "rgba(255,255,255,0.75)" }}>
                  We will create room types and rate plans from your PMS rooms so mapping is fast and clean.
                </Text>

                <Divider style={{ borderColor: "rgba(255,255,255,0.12)" }} />

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Badge count={pmsRooms.length} overflowCount={999} style={{ backgroundColor: "#52c41a" }}>
                    <span
                      style={{
                        padding: "8px 12px",
                        display: "inline-block",
                        background: "rgba(255,255,255,0.08)",
                        borderRadius: 12,
                      }}
                    >
                      PMS Room Types
                    </span>
                  </Badge>

                  <span
                    style={{
                      padding: "8px 12px",
                      display: "inline-block",
                      background: "rgba(255,255,255,0.08)",
                      borderRadius: 12,
                    }}
                  >
                    Default rate plan per room type
                  </span>

                  <span
                    style={{
                      padding: "8px 12px",
                      display: "inline-block",
                      background: "rgba(255,255,255,0.08)",
                      borderRadius: 12,
                    }}
                  >
                    Safe to re-run
                  </span>
                </div>
              </Card>

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                <Button onClick={() => setStep(1)} style={{ borderRadius: 12 }}>
                  Back
                </Button>
                <Button
                  type="primary"
                  loading={loading}
                  onClick={handleConnectAndProvision}
                  style={{ borderRadius: 12, fontWeight: 900 }}
                >
                  Provision & Continue
                </Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <Alert
                type="info"
                showIcon
                message="Complete mapping and activation"
                description="Open the embedded setup, complete connection testing, map rooms and rates, save, and activate the channel."
                style={{ marginBottom: 14, borderRadius: 14 }}
              />

              <Button
                type="primary"
                loading={loading}
                onClick={openEmbeddedSetup}
                style={{ borderRadius: 12, fontWeight: 900 }}
              >
                Open Channel Setup
              </Button>

              <Divider />

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Button onClick={() => setStep(2)} style={{ borderRadius: 12 }}>
                  Back
                </Button>
                <Button
                  type="primary"
                  onClick={() => setStep(4)}
                  style={{ borderRadius: 12, fontWeight: 900 }}
                >
                  I finished setup → Verify
                </Button>
              </div>

              <ChannexIframeModal
                open={iframeOpen}
                onClose={() => setIframeOpen(false)}
                url={iframeUrl}
                title="Channel Setup"
              />
            </>
          )}

          {step === 4 && (
            <>
              <Alert
                type="success"
                showIcon
                message="Verify booking sync"
                description="This checks whether booking revisions are arriving. It does not acknowledge them yet."
                style={{ marginBottom: 14, borderRadius: 14 }}
              />

              {verifyResult && (
                <Card size="small" style={{ borderRadius: 14, marginBottom: 14, background: "#fafafa" }}>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Tag color="blue">Found: {verifyResult.count || 0}</Tag>
                    {verifyResult.provider_code ? <Tag>{verifyResult.provider_code}</Tag> : null}
                    {verifyResult.property_id ? <Tag>{verifyResult.property_id}</Tag> : null}
                  </div>
                </Card>
              )}

              <div style={{ display: "flex", gap: 12 }}>
                <Button onClick={() => setStep(3)} style={{ borderRadius: 12 }}>
                  Back
                </Button>
                <Button
                  type="primary"
                  loading={loading}
                  onClick={verifySync}
                  style={{ borderRadius: 12, fontWeight: 900 }}
                >
                  Check Booking Feed
                </Button>
              </div>
            </>
          )}
        </Card>

        <style>{`
          .ns-stepsWrap{
            padding: 10px 12px;
            border-radius: 16px;
            background: linear-gradient(to bottom, rgba(15,23,42,.03), rgba(15,23,42,.01));
            border: 1px solid rgba(15,23,42,.08);
          }
          .ns-stepsWrap .ant-steps-item-title{
            font-weight: 900;
          }
          .ns-stepsWrap .ant-steps-item-description{
            color: rgba(15,23,42,.55);
          }
          .ns-stepsWrap .ant-steps-item-icon{
            transform: scale(1.10);
          }
          .ns-stepsWrap .ant-steps-item-process .ant-steps-item-icon{
            box-shadow: 0 14px 34px rgba(59,130,246,.22);
          }
        `}</style>
      </div>
    </div>
  );
}